/**
 * Hand-set flavor scores for the ingredients brewers judge by (D-FL4).
 *
 * An entry replaces the rule's value on each axis it names and leaves the
 * rest to the rule. Kept short on purpose: an override is a claim somebody
 * will argue with, so each one should be a claim worth making.
 */

import type { AxisScores } from '@beerwright/core';

export const HOP_OVERRIDES: Readonly<Record<string, AxisScores<'hop'>>> = {
  saaz: { floral: 4, herbal: 3, spice: 3, grassy: 2, earthy: 1, citrus: 1 },
  'hallertauer-mittelfrueh': { floral: 4, herbal: 3, spice: 2, grassy: 1, citrus: 1 },
  // The rule reads Sterling's `citrus` tag as a loud note, and the variety
  // is Saaz-derived: its citrus is a hint, and at 3 it kept Sterling off
  // every Czech and German lager's hop list (PLAN-STYLE-PAGES).
  sterling: { citrus: 2 },
  // Our own description calls its blackcurrant faint; at 3 it kept the hop
  // that bittered pre-Prohibition lager off that style's page.
  cluster: { berry: 2 },
  // Noble and continental types held off lager and ale pages by one
  // secondary note the tag rule scores 3 only because it is the third or
  // fourth tag (PLAN-STYLE-PAGES). Each is a hint, not a lead, so 2. Left at
  // 3 on purpose: Styrian Goldings' lemon, Smaragd's fruit, Opal's citrus.
  'saaz-washington': { citrus: 2 },
  mystique: { citrus: 2 },
  celeia: { citrus: 2 },
  aurora: { citrus: 2 },
  aramis: { citrus: 2 },
  'us-goldings': { citrus: 2 },
  'uk-golding': { citrus: 2 },
  crystal: { earthy: 2 },
  vanguard: { earthy: 2 },
  wgv: { earthy: 2 },
  hersbrucker: { grassy: 2 },
  lublin: { grassy: 2 },
  strisselspalt: { stoneFruit: 2 },
  sladek: { stoneFruit: 2 },
  willamette: { berry: 2 },
  'east-kent-goldings': { earthy: 4, floral: 3, herbal: 2, spice: 2, citrus: 1 },
  magnum: { herbal: 2, spice: 2, resinPine: 2, floral: 1 },
  cascade: { citrus: 4, floral: 3, resinPine: 2, spice: 1 },
  citra: { citrus: 5, tropical: 5, stoneFruit: 2, resinPine: 1 },
  mosaic: { tropical: 4, berry: 4, citrus: 3, resinPine: 3, earthy: 1 },
  simcoe: { resinPine: 5, citrus: 3, tropical: 2, earthy: 2 },
};

export const FERMENTABLE_OVERRIDES: Readonly<Record<string, AxisScores<'malt'>>> = {
  'bestmalz-roasted-barley': { roast: 5, chocolate: 1 },
  'dingemans-special-b': { darkFruit: 5, caramel: 3, roast: 1 },
};

export const YEAST_OVERRIDES: Readonly<Record<string, AxisScores<'fermentation'>>> = {
  'fermentis-w-34-70': { clean: 5, sulfur: 1, fruity: 0 },
  'fermentis-us-05': { clean: 4, fruity: 1 },
  'fermentis-s-04': { clean: 2, fruity: 3 },
  'fermentis-wb-06': { banana: 3, clove: 3, fruity: 2, clean: 1 },
  'wyeast-3068': { banana: 4, clove: 3, fruity: 2, clean: 0 },
  'white-labs-wlp300': { banana: 4, clove: 3, fruity: 2, clean: 0 },
  'wyeast-3724': { pepper: 4, fruity: 3, clove: 1, clean: 0 },
  'wyeast-3711': { pepper: 3, fruity: 3, clove: 1, clean: 1 },
  'lallemand-belle-saison': { pepper: 3, fruity: 3, clean: 1 },
  'fermentis-be-256': { fruity: 4, pepper: 2, banana: 1, clean: 1 },
  'wyeast-1318': { fruity: 4, clean: 2 },
};
